/**
 * Chat Error Modal
 *
 * Enhanced error modal for chat errors with user-friendly messages
 * and quick action buttons based on the error type.
 */

import { AlertTriangle, Info, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils/cn';
import type { ErrorAction, MappedError } from '../utils/openai-error-mapper';
import { Button } from './button';
import { Dialog } from './dialog';
import { IconButton } from './icon-button';

export type ChatErrorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  error: MappedError;
  onAction: (actionId: ErrorAction) => void;
  className?: string;
};

export const ChatErrorModal: React.FC<ChatErrorModalProps> = ({
  isOpen,
  onClose,
  error,
  onAction,
  className,
}) => {
  // Handle action click
  const handleActionClick = (actionId: ErrorAction) => {
    onAction(actionId);
    // Close modal after action is clicked (unless it's retry)
    if (actionId !== 'retry') {
      onClose();
    }
  };

  // Select icon based on severity
  const Icon =
    error.severity === 'error' ? XCircle : error.severity === 'warning' ? AlertTriangle : Info;

  const iconColor =
    error.severity === 'error'
      ? 'text-red-600 dark:text-red-400'
      : error.severity === 'warning'
        ? 'text-orange-600 dark:text-orange-400'
        : 'text-blue-600 dark:text-blue-400';

  const iconBgColor =
    error.severity === 'error'
      ? 'bg-red-100 dark:bg-red-900/20'
      : error.severity === 'warning'
        ? 'bg-orange-100 dark:bg-orange-900/20'
        : 'bg-blue-100 dark:bg-blue-900/20';

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      aria-labelledby="chat-error-title"
      className={cn('p-6', className)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={cn('w-10 h-10 rounded-full flex items-center justify-center', iconBgColor)}
          >
            <Icon className={cn('w-5 h-5', iconColor)} />
          </div>
          <h2 id="chat-error-title" className="text-lg font-semibold text-foreground">{error.title}</h2>
        </div>
        <IconButton
          iconName="X"
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Close error modal"
        />
      </div>

      {/* Content */}
      <div className="mb-6">
        <p className="text-sm text-muted-foreground leading-relaxed">{error.message}</p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {error.actions.map((action, index) => (
          <Button
            key={action.id}
            onClick={() => handleActionClick(action.id)}
            variant={action.variant || 'default'}
            className="w-full"
            autoFocus={index === 0}
          >
            {action.label}
          </Button>
        ))}

        {error.actions.length > 0 && (
          <Button onClick={onClose} variant="ghost" className="w-full">
            Dismiss
          </Button>
        )}
      </div>
    </Dialog>
  );
};

ChatErrorModal.displayName = 'ChatErrorModal';
